import { useState, useRef, useEffect } from "react";
import { 
  MessageCircle, X, Send, Plus, Trash2, 
  ChevronLeft, Loader2, Bot, User, Sparkles
} from "lucide-react";
import { api } from "@/App";
import { useAuth } from "@/context/AuthContext";

const MentorChat = () => {
  const { isTrialing } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on Esc + lock body scroll while the panel is open (restore on close).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const loadSessions = async () => {
    try {
      const data = await api.get("/chat/sessions");
      setSessions(data.sessions || []);
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  };

  const loadSession = async (sid) => {
    try {
      const data = await api.get(`/chat/sessions/${sid}/messages`);
      setMessages(data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch (e) {
      console.error("Failed to load session", e);
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete ? await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/sessions/${sid}`, { method: 'DELETE' }) : null;
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        setMessages([]);
        setSessionId(null);
      }
    } catch (e) {
      console.error("Failed to delete session", e);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMsg = { role: "user", content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const data = await api.post("/chat/send", {
        message: userMsg.content,
        session_id: sessionId
      });
      
      if (!sessionId) setSessionId(data.session_id);
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString()
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: e?.response?.status === 402
          ? "You've used your 3 free AI tutor messages for today. Upgrade to Pro for unlimited AI tutoring - or come back tomorrow."
          : "Sorry, I'm having trouble connecting. Please try again.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatContent = (text) => {
    // Simple markdown-like formatting
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/### (.*?)(\n|$)/g, '<div class="text-sm font-semibold text-primary mt-3 mb-1">$1</div>')
      .replace(/## (.*?)(\n|$)/g, '<div class="text-sm font-bold text-primary mt-3 mb-1">$1</div>')
      .replace(/\n- /g, '\n• ')
      .replace(/\n/g, '<br/>');
  };

  const quickPrompts = [
    "Explain the classification of malocclusion",
    "What are the types of dental cements?",
    "Key points about periapical pathology",
    "Drug interactions in dental patients",
  ];

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && (
        <button
          data-testid="mentor-chat-toggle"
          onClick={() => { setIsOpen(true); loadSessions(); }}
          className="fixed bottom-24 md:bottom-16 right-4 md:right-6 z-40 md:z-[60] w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center shadow-2xl shadow-primary/25 transition-all hover:scale-105 active:scale-95"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Backdrop - tap/click outside closes (also dims on desktop) */}
      {isOpen && (
        <div
          data-testid="mentor-chat-backdrop"
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-[59] bg-black/50 sm:bg-black/30"
        />
      )}

      {/* Chat panel */}
      {isOpen && (
        <div 
          data-testid="mentor-chat-panel"
          className="fixed inset-0 z-[60] w-full h-[100dvh] sm:inset-auto sm:bottom-16 sm:right-6 sm:w-[420px] sm:h-[650px] sm:max-h-[calc(100dvh-5rem)] sm:rounded-2xl flex flex-col overflow-hidden border border-border shadow-2xl"
          style={{ background: '#0d1220' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 z-10 flex-shrink-0" style={{ background: '#111827', paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-3">
              {showSessions ? (
                <button onClick={() => setShowSessions(false)} className="p-1 hover:bg-secondary rounded-lg transition-colors">
                  <ChevronLeft className="w-5 h-5 text-muted-foreground" />
                </button>
              ) : (
                <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-primary" />
                </div>
              )}
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {showSessions ? "Chat History" : "Dr. ADC Mentor"}
                </h3>
                {!showSessions && (
                  <p className="text-xs text-muted-foreground">Personal tutor{isTrialing ? " · in your Pro trial" : ""}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!showSessions && (
                <>
                  <button
                    data-testid="mentor-chat-history-btn"
                    onClick={() => { setShowSessions(true); loadSessions(); }}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    title="Chat history"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
                  </button>
                  <button
                    data-testid="mentor-chat-new-btn"
                    onClick={startNewChat}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    title="New chat"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                data-testid="mentor-chat-close"
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sessions list */}
          {showSessions ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8">No conversations yet</p>
              ) : (
                sessions.map(s => (
                  <button
                    key={s.session_id}
                    data-testid={`chat-session-${s.session_id}`}
                    onClick={() => loadSession(s.session_id)}
                    className="w-full text-left p-3 rounded-xl hover:bg-secondary border border-border transition-colors group"
                  >
                    <div className="flex justify-between items-start">
                      <p className="text-sm text-foreground font-medium truncate flex-1">{s.title}</p>
                      <button
                        onClick={(e) => deleteSession(s.session_id, e)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive/20 rounded transition-all"
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{s.last_message}</p>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <h4 className="text-base font-semibold text-foreground mb-1">How can I help you study?</h4>
                    <p className="text-sm text-muted-foreground mb-6">Ask me anything about ADC Part 1 topics</p>
                    <div className="space-y-2 w-full">
                      {quickPrompts.map((prompt, i) => (
                        <button
                          key={i}
                          data-testid={`quick-prompt-${i}`}
                          onClick={() => { setInput(prompt); }}
                          className="w-full text-left text-sm p-3 rounded-xl border border-border hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((msg, i) => (
                    <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div 
                        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                          msg.role === 'user' 
                            ? 'bg-primary text-primary-foreground rounded-br-md' 
                            : 'bg-secondary text-foreground border border-border rounded-bl-md'
                        }`}
                        dangerouslySetInnerHTML={
                          msg.role === 'assistant' 
                            ? { __html: formatContent(msg.content) }
                            : undefined
                        }
                      >
                        {msg.role === 'user' ? msg.content : undefined}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  ))
                )}
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-secondary border border-border rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <div className="p-3 border-t border-border flex-shrink-0" style={{ background: '#111827', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                <div className="flex items-end gap-2 bg-secondary border border-border rounded-xl p-2">
                  <textarea
                    ref={inputRef}
                    data-testid="mentor-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about any dental topic..."
                    rows={1}
                    className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none max-h-24 px-2 py-1.5"
                    style={{ minHeight: '36px' }}
                  />
                  <button
                    data-testid="mentor-chat-send"
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="p-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground/50 text-center mt-2">
                  Responses are for study purposes. Always verify with official guidelines.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default MentorChat;
